import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

// * get ucl subscriptions
export const getUclSubscriptionList = createAsyncThunk(
  "uclSubscription/get-ucl-subscriptions-list",
  async (
    {
      page,
      currentPage,
      limit,
    }: { page: number; currentPage: number; limit: number },
    { rejectWithValue },
  ) => {
    try {
      const result = await axios.get(`get-ucl-subscriptions-list`, {
        withCredentials: true,
        params: {
          page,
          currentPage,
          limit,
        },
      });
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// ** create ucl subscription
export const createUclSubscription = createAsyncThunk(
  "uclSubscription/create-ucl-subscription",
  async (subscriptionData: any, { rejectWithValue }) => {
    try {
      const result = await axios.post(
        `create-ucl-subscription`,
        subscriptionData,
        {
          withCredentials: true,
        },
      );
      if (result.data.Success) {
        toast.success(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
      return result.data;
    } catch (error: any) {
      toast.error(error.response.data.Description, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return rejectWithValue(error.response.data);
    }
  },
);

// ** update ucl subscription
export const updateUclSubscription = createAsyncThunk(
  "uclSubscription/update-ucl-subscription",
  async (
    { subscriptionId, data }: { subscriptionId: any; data: any },
    { rejectWithValue },
  ) => {
    try {
      const result = await axios.put(
        `update-ucl-subscription/${subscriptionId}`,
        data,
        {
          withCredentials: true,
        },
      );
      if (result.data.Success) {
        toast.success(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
      return result.data;
    } catch (error: any) {
      toast.error(error.response.data.Description, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return rejectWithValue(error.response.data);
    }
  },
);

// ** cancel ucl subscription
export const cancelUclSubscription = createAsyncThunk(
  "uclSubscription/cancel-ucl-subscription",
  async (
    { subscriptionId, data }: { subscriptionId: String | undefined; data: any },
    { rejectWithValue },
  ) => {
    try {
      const result = await axios.put(
        `cancel-ucl-subscription/${subscriptionId}`,
        data,
        {
          withCredentials: true,
        },
      );
      if (result.data.Success) {
        toast.success(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
      return result.data;
    } catch (error: any) {
      toast.error(error.response.data.Description, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return rejectWithValue(error.response.data);
    }
  },
);

// ** resend ucl subscription
export const resendUclSubscription = createAsyncThunk(
  "uclSubscription/resend-ucl-subscription",
  async (data: any, { rejectWithValue }) => {
    try {
      const result = await axios.post(`resend-ucl-subscription`, data, {
        withCredentials: true,
      });
      if (result.data.Success) {
        toast.success(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// ** get ucl archived subscriptions
export const getUclArchivedSubscriptionList = createAsyncThunk(
  "uclSubscription/get-archived-ucl-subscriptions-list",
  async (
    {
      page,
      currentPage,
      limit,
    }: { page: number; currentPage: number; limit: number },
    { rejectWithValue },
  ) => {
    try {
      const result = await axios.get(`get-archived-ucl-subscriptions-list`, {
        withCredentials: true,
        params: {
          page,
          currentPage,
          limit,
        },
      });
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// ** delete ucl subscription
export const deleteUclSubscription = createAsyncThunk(
  "uclSubscription/delete-ucl-subscription",
  async (
    { subscriptionId, data }: { subscriptionId: String | undefined; data: any },
    { rejectWithValue },
  ) => {
    try {
      const result = await axios.put(
        `delete-ucl-subscription/${subscriptionId}`,
        data,
        {
          withCredentials: true,
        },
      );

      if (result.data.Success) {
        toast.success(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// ** export ucl subscription
export const exportUclSubscriptionsList = createAsyncThunk(
  "uclSubscription/export-ucl-subscriptions-list",
  async (data: any, { rejectWithValue }) => {
    try {
      const result = await axios.post(`export-ucl-subscriptions-list`, data, {
        withCredentials: true,
      });
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// ** filter ucl subscriptions
export const filterUclSubscriptions = createAsyncThunk(
  "uclSubscription/filter-ucl-subscriptions-list",
  async (
    {
      query,
      carType,
      status,
      startDate,
      endDate,
      subscriptionType,
      actionType,
    }: {
      query: string | null;
      carType: string;
      status: string;
      startDate: string | Date | null;
      endDate: string | Date | null;
      subscriptionType: string | null;
      actionType: "FILTER" | "RESET" | "FETCH" | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const result = await axios.get(`filter-ucl-subscriptions-list`, {
        withCredentials: true,
        params: {
          query,
          carType,
          status,
          startDate,
          endDate,
          subscriptionType,
          actionType,
        },
      });
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// ** filter archived ucl subscriptions
export const filterUclArchivedSubscriptions = createAsyncThunk(
  "uclSubscription/filter-archived-ucl-subscriptions-list",
  async (
    {
      query,
      carType,
      status,
      startDate,
      endDate,
      subscriptionType,
      actionType,
    }: {
      query: string | null;
      carType: string;
      status: string;
      startDate: string | Date | null;
      endDate: string | Date | null;
      subscriptionType: string | null;
      actionType: "FILTER" | "RESET" | "FETCH" | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const result = await axios.get(`filter-archived-ucl-subscriptions-list`, {
        withCredentials: true,
        params: {
          query,
          carType,
          status,
          startDate,
          endDate,
          subscriptionType,
          actionType,
        },
      });
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// * unarchive ucl subscription
export const unarchiveUclSubscription = createAsyncThunk(
  "uclSubscription/unarchive-ucl-subscription",
  async (
    { subscriptionId, data }: { subscriptionId: String | undefined; data: any },
    { rejectWithValue },
  ) => {
    try {
      const result = await axios.put(
        `unarchive-ucl-subscription/${subscriptionId}`,
        data,
        {
          withCredentials: true,
        },
      );

      if (result.data.Success) {
        toast.success(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// * export ucl subscription
export const exportArchivedUclSubscriptionsList = createAsyncThunk(
  "uclSubscription/export-archived-ucl-subscriptions-list",
  async (data: any, { rejectWithValue }) => {
    try {
      const result = await axios.post(
        `export-archived-ucl-subscriptions-list`,
        data,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// ** delete multiple ucl subscription
export const deleteMultipleUclSubscriptions = createAsyncThunk(
  "uclSubscription/delete-multiple-ucl-subscriptions",
  async (data: any, { rejectWithValue }) => {
    try {
      const result = await axios.put(
        `delete-multiple-ucl-subscriptions`,
        data,
        {
          withCredentials: true,
        },
      );

      if (result.data.Success) {
        toast.success(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(result.data.Description, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

const uclSubscription = createSlice({
  name: "uclSubscription",
  initialState: {
    subscription: {},
    subscriptions: [],
    loading: false,
    success: false,
    description: null,
    exportedSubscriptions: [],
    pagination: null,
  },
  reducers: {
    clearErrors: (state) => {
      state.description = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // * get ucl subscription list
      .addCase(getUclSubscriptionList.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.description = null;
      })
      .addCase(getUclSubscriptionList.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload.Subscriptions;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
        state.pagination = action.payload.Pagination;
      })
      .addCase(getUclSubscriptionList.rejected, (state: any, action: any) => {
        state.loading = false;

        state.success = false;
        state.description = action.payload.Description;
      })
      // * get ucl archived subscription list
      .addCase(getUclArchivedSubscriptionList.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.description = null;
      })
      .addCase(getUclArchivedSubscriptionList.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload.Subscriptions;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
        state.pagination = action.payload.Pagination;
      })
      .addCase(
        getUclArchivedSubscriptionList.rejected,
        (state: any, action: any) => {
          state.loading = false;
          state.success = false;
          state.description = action.payload.Description;
        },
      )
      // * create ucl subscription
      .addCase(createUclSubscription.pending, (state) => {
        state.success = false;
        state.loading = true;
        state.description = null;
      })
      .addCase(createUclSubscription.fulfilled, (state, action) => {
        state.success = action.payload.Success;
        state.loading = false;
        state.subscriptions = action.payload.Subscriptions;
        state.description = action.payload.Description;
        state.pagination = action.payload.Pagination;
      })
      .addCase(createUclSubscription.rejected, (state: any, action: any) => {
        state.loading = false;
        state.success = false;
        state.description = action.payload.Description;
      })
      // * update ucl subscription
      .addCase(updateUclSubscription.pending, (state) => {
        state.success = false;
        state.loading = true;
        state.description = null;
      })
      .addCase(updateUclSubscription.fulfilled, (state, action) => {
        state.success = action.payload.Success;
        state.loading = false;
        state.subscriptions = action.payload.Subscriptions;
        state.description = action.payload.Description;
        state.pagination = action.payload.Pagination;
      })
      .addCase(updateUclSubscription.rejected, (state: any, action: any) => {
        state.loading = false;
        state.success = false;
        state.description = action.payload.Description;
      })
      // * cancel ucl subscription
      .addCase(cancelUclSubscription.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.description = null;
      })
      .addCase(cancelUclSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload.Subscriptions;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
        state.pagination = action.payload.Pagination;
      })
      .addCase(cancelUclSubscription.rejected, (state: any, action: any) => {
        state.loading = false;
        state.success = false;
        state.description = action.payload.Description;
      })
      // * delete ucl subscription
      .addCase(deleteUclSubscription.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.description = null;
      })
      .addCase(deleteUclSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload.Subscriptions;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
        state.pagination = action.payload.Pagination;
      })
      .addCase(deleteUclSubscription.rejected, (state: any, action: any) => {
        state.loading = false;

        state.success = false;
        state.description = action.payload.Description;
      })
      // * resend ucl subscription
      .addCase(resendUclSubscription.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.description = null;
      })
      .addCase(resendUclSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
      })
      .addCase(resendUclSubscription.rejected, (state: any, action: any) => {
        state.loading = false;
        state.success = false;
        state.description = action.payload.Description;
      })
      // * export ucl subscription
      .addCase(exportUclSubscriptionsList.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.description = null;
      })
      .addCase(exportUclSubscriptionsList.fulfilled, (state, action) => {
        state.loading = false;
        state.exportedSubscriptions = action.payload.Subscriptions;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
      })
      .addCase(
        exportUclSubscriptionsList.rejected,
        (state: any, action: any) => {
          state.loading = false;
          state.success = false;
          state.description = action.payload.Description;
        },
      )
      // * filter ucl subscriptions
      .addCase(filterUclSubscriptions.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.description = null;
      })
      .addCase(filterUclSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload.Subscriptions;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
        state.pagination = action.payload.pagination;
      })
      .addCase(filterUclSubscriptions.rejected, (state: any, action: any) => {
        state.loading = false;
        state.success = false;
        state.description = action.payload.Description;
      })
      // * filter archived ucl subscriptions
      .addCase(filterUclArchivedSubscriptions.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.description = null;
      })
      .addCase(filterUclArchivedSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload.Subscriptions;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
        state.pagination = action.payload.pagination;
      })
      .addCase(
        filterUclArchivedSubscriptions.rejected,
        (state: any, action: any) => {
          state.loading = false;
          state.success = false;
          state.description = action.payload.Description;
        },
      )
      // * unarchive subscription
      .addCase(unarchiveUclSubscription.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.description = null;
      })
      .addCase(unarchiveUclSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload.Subscriptions;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
        state.pagination = action.payload.Pagination;
      })
      .addCase(unarchiveUclSubscription.rejected, (state: any, action: any) => {
        state.loading = false;
        state.success = false;
        state.description = action.payload.Description;
      })

      // * export archived ucl subscription
      .addCase(exportArchivedUclSubscriptionsList.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.description = null;
      })
      .addCase(
        exportArchivedUclSubscriptionsList.fulfilled,
        (state, action) => {
          state.loading = false;
          state.exportedSubscriptions = action.payload.Subscriptions;
          state.success = action.payload.Success;
          state.description = action.payload.Description;
        },
      )
      .addCase(
        exportArchivedUclSubscriptionsList.rejected,
        (state: any, action: any) => {
          state.loading = false;
          state.success = false;
          state.description = action.payload.Description;
        },
      )

      // * delete multiple ucl subscriptions
      .addCase(deleteMultipleUclSubscriptions.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.description = null;
      })
      .addCase(deleteMultipleUclSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload.Subscriptions;
        state.success = action.payload.Success;
        state.description = action.payload.Description;
        state.pagination = action.payload.Pagination;
      })
      .addCase(
        deleteMultipleUclSubscriptions.rejected,
        (state: any, action: any) => {
          state.loading = false;
          state.success = false;
          state.description = action.payload.Description;
        },
      );
  },
});

export default uclSubscription.reducer;
